import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { 
    SparklesIcon, 
    FireIcon, 
    TagIcon,
    HeartIcon,
    PlusIcon,
    TrashIcon,
    PencilIcon,
    EyeIcon
} from '@heroicons/react/24/outline';
import api from '../utils/api';

function CollectionManagementPanel() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [bulkAction, setBulkAction] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('all');

    // Koleksiyon tanımları
    const collections = {
        'yeni-sezon': {
            title: 'Yeni Sezon',
            icon: SparklesIcon,
            color: 'brand-green-light',
            description: 'Son 30 gün içinde eklenen ürünler',
            criteria: 'created_at >= 30 days ago'
        },
        'en-cok-satanlar': {
            title: 'En Çok Satanlar',
            icon: FireIcon,
            color: 'green',
            description: 'Fiyatı yüksek popüler ürünler',
            criteria: 'price >= 150'
        },
        'indirimli-urunler': {
            title: 'İndirimli Ürünler',
            icon: TagIcon,
            color: 'emerald',
            description: 'Fiyat uyarısı olan ürünler',
            criteria: 'price_warning_days > 0'
        },
        'minela-kids': {
            title: 'Minela Kids Özel',
            icon: HeartIcon,
            color: 'brand-green',
            description: 'Premium ürünler (200 TL üzeri)',
            criteria: 'price >= 200'
        }
    };

    useEffect(() => {
        fetchProducts();
    }, []);

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const response = await api('/api/products?limit=100');
            const productList = response.products || response.data || response || [];
            
            setProducts(productList);
            
            if (productList.length === 0) {
                toast.info('Henüz ürün bulunmuyor. Önce ürün ekleyerek koleksiyonları doldurmaya başlayın.');
            }
        } catch (error) {
            console.error('Ürünler yüklenirken hata:', error);
            toast.error(`Ürünler yüklenemedi: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const getCollectionProducts = (collectionKey) => {
        if (!Array.isArray(products)) return [];
        
        const now = new Date();
        const thirtyDaysAgo = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));

        switch (collectionKey) {
            case 'yeni-sezon':
                return products.filter(p => new Date(p.created_at) >= thirtyDaysAgo);
            case 'en-cok-satanlar':
                return products.filter(p => p.price >= 150);
            case 'indirimli-urunler':
                return products.filter(p => p.price_warning_days > 0);
            case 'minela-kids':
                return products.filter(p => p.price >= 200);
            default:
                return [];
        }
    };

    const updateProductForCollection = async (productId, field, value) => {
        try {
            await api(`/api/products/${productId}`, {
                method: 'PATCH',
                body: JSON.stringify({ [field]: value })
            });
            
            // Local state'i güncelle
            setProducts(prev => prev.map(p => 
                p.id === productId ? { ...p, [field]: value } : p
            ));
            
            toast.success('Ürün güncellendi');
        } catch (error) {
            console.error('Ürün güncellenirken hata:', error);
            toast.error('Ürün güncellenemedi');
        }
    };

    const bulkUpdateProducts = async () => {
        if (!bulkAction || selectedProducts.length === 0) {
            toast.warning('Lütfen ürün seçin ve işlem belirleyin');
            return;
        }

        try {
            const updates = selectedProducts.map(productId => {
                switch (bulkAction) {
                    case 'add-to-new-season':
                        // Yeni sezon için created_at'i güncelle
                        return api(`/api/products/${productId}`, {
                            method: 'PATCH',
                            body: JSON.stringify({ created_at: new Date().toISOString() })
                        });
                    case 'add-to-bestseller':
                        // Bestseller için fiyatı artır
                        const product = products.find(p => p.id === productId);
                        const newPrice = Math.max(150, product.price);
                        return api(`/api/products/${productId}`, {
                            method: 'PATCH',
                            body: JSON.stringify({ price: newPrice })
                        });
                    case 'add-to-sale-10':
                        return api(`/api/products/${productId}`, {
                            method: 'PATCH',
                            body: JSON.stringify({ 
                                price_warning_days: 7,
                                discount_percentage: 10
                            })
                        });
                    case 'add-to-sale-20':
                        return api(`/api/products/${productId}`, {
                            method: 'PATCH',
                            body: JSON.stringify({ 
                                price_warning_days: 7,
                                discount_percentage: 20
                            })
                        });
                    case 'add-to-sale-30':
                        return api(`/api/products/${productId}`, {
                            method: 'PATCH',
                            body: JSON.stringify({ 
                                price_warning_days: 7,
                                discount_percentage: 30
                            })
                        });
                    case 'add-to-sale-50':
                        return api(`/api/products/${productId}`, {
                            method: 'PATCH',
                            body: JSON.stringify({ 
                                price_warning_days: 7,
                                discount_percentage: 50
                            })
                        });
                    case 'remove-discount':
                        return api(`/api/products/${productId}`, {
                            method: 'PATCH',
                            body: JSON.stringify({ 
                                price_warning_days: 0,
                                discount_percentage: 0
                            })
                        });
                    case 'add-to-premium':
                        // Premium için fiyatı artır
                        const premiumProduct = products.find(p => p.id === productId);
                        const premiumPrice = Math.max(200, premiumProduct.price);
                        return api(`/api/products/${productId}`, {
                            method: 'PATCH',
                            body: JSON.stringify({ price: premiumPrice })
                        });
                    default:
                        return Promise.resolve();
                }
            });

            await Promise.all(updates);
            await fetchProducts(); // Ürünleri yeniden yükle
            setSelectedProducts([]);
            setBulkAction('');
            toast.success(`${selectedProducts.length} ürün güncellendi`);
        } catch (error) {
            console.error('Toplu güncelleme hatası:', error);
            toast.error('Toplu güncelleme başarısız');
        }
    };

    const filteredProducts = (products || []).filter(product => {
        const matchesSearch = product.name?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = filterCategory === 'all' || 
            product.categories?.some(cat => cat.slug === filterCategory);
        return matchesSearch && matchesCategory;
    });

    if (loading) {
        return <div className="text-center py-8">Ürünler yükleniyor...</div>;
    }

    return (
        <div className="space-y-6">
            {/* Koleksiyon Özeti */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {Object.entries(collections).map(([key, collection]) => {
                    const collectionProducts = getCollectionProducts(key);
                    const IconComponent = collection.icon;
                    
                    return (
                        <div key={key} className="bg-white rounded-lg shadow-md p-4 border-l-4 border-l-gray-400">
                            <div className="flex items-center justify-between mb-2">
                                <IconComponent className={`w-6 h-6 text-${collection.color}-500`} />
                                <span className="text-2xl font-bold text-gray-800">
                                    {collectionProducts.length}
                                </span>
                            </div>
                            <h3 className="font-semibold text-gray-800 mb-1">{collection.title}</h3>
                            <p className="text-xs text-gray-600 mb-2">{collection.description}</p>
                            <a 
                                href={`/collections/${key}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center text-xs text-blue-600 hover:text-blue-800"
                            >
                                <EyeIcon className="w-4 h-4 mr-1" />
                                Görüntüle
                            </a>
                        </div>
                    );
                })}
            </div>

            {/* Filtreler ve Toplu İşlemler */}
            <div className="bg-white rounded-lg shadow-md p-4">
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <input
                            type="text"
                            placeholder="Ürün ara..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        
                        <select
                            value={filterCategory}
                            onChange={(e) => setFilterCategory(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="all">Tüm Kategoriler</option>
                            <option value="kiz-cocuk">Kız Çocuk</option>
                            <option value="erkek-cocuk">Erkek Çocuk</option>
                            <option value="bebek">Bebek</option>
                            <option value="ayakkabi">Ayakkabı</option>
                            <option value="aksesuar">Aksesuar</option>
                        </select>
                    </div>

                    {selectedProducts.length > 0 && (
                        <div className="flex gap-2">
                            <select
                                value={bulkAction}
                                onChange={(e) => setBulkAction(e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">İşlem Seçin</option>
                                <option value="add-to-new-season">Yeni Sezon'a Ekle</option>
                                <option value="add-to-bestseller">En Çok Satan'a Ekle</option>
                                <option value="add-to-sale-10">%10 İndirim Ekle</option>
                                <option value="add-to-sale-20">%20 İndirim Ekle</option>
                                <option value="add-to-sale-30">%30 İndirim Ekle</option>
                                <option value="add-to-sale-50">%50 İndirim Ekle</option>
                                <option value="remove-discount">İndirimi Kaldır</option>
                                <option value="add-to-premium">Premium'a Ekle</option>
                            </select>
                            
                            <button
                                onClick={bulkUpdateProducts}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                            >
                                Uygula ({selectedProducts.length})
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Ürün Listesi */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left">
                                    <input
                                        type="checkbox"
                                        checked={selectedProducts.length === filteredProducts.length && filteredProducts.length > 0}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedProducts(filteredProducts.map(p => p.id));
                                            } else {
                                                setSelectedProducts([]);
                                            }
                                        }}
                                        className="rounded border-gray-300"
                                    />
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ürün</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fiyat</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">İndirim %</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">İndirim Günü</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Koleksiyonlar</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">İşlemler</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {filteredProducts.map((product) => {
                                const productCollections = Object.entries(collections).filter(([key]) => 
                                    getCollectionProducts(key).some(p => p.id === product.id)
                                );

                                return (
                                    <tr key={product.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3">
                                            <input
                                                type="checkbox"
                                                checked={selectedProducts.includes(product.id)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedProducts(prev => [...prev, product.id]);
                                                    } else {
                                                        setSelectedProducts(prev => prev.filter(id => id !== product.id));
                                                    }
                                                }}
                                                className="rounded border-gray-300"
                                            />
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center">
                                                <img 
                                                    src={product.main_image_url || '/placeholder.jpg'} 
                                                    alt={product.name}
                                                    className="w-10 h-10 rounded-md object-cover mr-3"
                                                />
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">{product.name}</div>
                                                    <div className="text-sm text-gray-500">ID: {product.id}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-col">
                                                <input
                                                    type="number"
                                                    value={product.price}
                                                    onChange={(e) => updateProductForCollection(product.id, 'price', parseFloat(e.target.value))}
                                                    className="w-20 px-2 py-1 text-sm border border-gray-300 rounded mb-1"
                                                />
                                                {product.original_price && product.original_price !== product.price && (
                                                    <span className="text-xs text-gray-500 line-through">
                                                        {product.original_price} TL
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <input
                                                type="number"
                                                min="0"
                                                max="100"
                                                value={product.discount_percentage || 0}
                                                onChange={(e) => updateProductForCollection(product.id, 'discount_percentage', parseFloat(e.target.value))}
                                                className="w-16 px-2 py-1 text-sm border border-gray-300 rounded"
                                            />
                                        </td>
                                        <td className="px-4 py-3">
                                            <input
                                                type="number"
                                                value={product.price_warning_days || 0}
                                                onChange={(e) => updateProductForCollection(product.id, 'price_warning_days', parseInt(e.target.value))}
                                                className="w-16 px-2 py-1 text-sm border border-gray-300 rounded"
                                            />
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-wrap gap-1">
                                                {productCollections.map(([key, collection]) => {
                                                    const IconComponent = collection.icon;
                                                    return (
                                                        <span 
                                                            key={key}
                                                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-${collection.color}-100 text-${collection.color}-800`}
                                                        >
                                                            <IconComponent className="w-3 h-3 mr-1" />
                                                            {collection.title}
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <a
                                                href={`/products/${product.id}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:text-blue-800"
                                            >
                                                <EyeIcon className="w-4 h-4" />
                                            </a>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {filteredProducts.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                    Ürün bulunamadı
                </div>
            )}
        </div>
    );
}

export default CollectionManagementPanel;